
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import TaskResetDialog from '@/components/TaskResetDialog.vue';
import TaskSuperActions from '@/components/TaskSuperActions.vue';
import {
  Lead, Task, StepNames, User, PermissionCode, LeadStatus, ProjectsPool, LeadType, Network,
} from '@/types';
import {
  canEditTaskStatus, canResetTask, currentUserHasPermission, selectedLeadStatusIs,
} from '@/helpers';

@Component({
  components: {
    TaskResetDialog,
    TaskSuperActions,
  },
})
export default class TaskActions extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop(String) readonly step: StepNames | undefined;
  @Prop({ type: Boolean, default: false }) readonly errorOnly!: boolean;
  @Prop({ type: String, default: 'success' }) readonly completeColor!: string;
  @Prop({ type: Boolean, default: false }) readonly completeDark!: boolean;
  @Prop({ type: String, default: 'red' }) readonly errorColor!: string;
  @Prop({ type: String, default: 'Impossibile completare?' }) readonly errorText!: string;

  resetDialog = false

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati.
      return childLeads[this.task.childLeadId];
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  // Sto caricando il Task
  get loading(): boolean {
    return this.$store.getters.isTaskLoading;
  }

  // Sto aggiornando il Task
  get updating(): boolean {
    return this.$store.getters.isTaskUpdating;
  }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? null; }

  get valid(): boolean {
    return this.$store.getters.isTaskValid;
  }

  // È necessario salvare le modifiche al Task?
  get dirty(): boolean {
    return this.$store.getters.isTaskDirty;
  }

  get disabled(): boolean {
    return !this.valid;
  }

  get status(): LeadStatus | null {
    return this.selectedLead?.status ?? null;
  }

  get open(): boolean {
    return selectedLeadStatusIs('open');
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get isSuperUser(): boolean {
    return this.user && this.user.isSuperUser;
  }

  get networks(): Network[] {
    return this.$store.getters.networks;
  }

  get isComplete(): boolean {
    if (this.selectedTask) return this.selectedTask.status === 'completed';
    if (this.selectedLead && this.step) return this.selectedLead[this.step] === true;
    return false;
  }

  get isFailed(): boolean {
    if (this.selectedTask) return this.selectedTask.status === 'error';
    if (this.selectedLead && this.step) return this.selectedLead[this.step] === false;
    return false;
  }

  get canResetTask(): boolean {
    return canResetTask(this.selectedTask, this.selectedLead);
  }

  get canEditTaskStatus(): boolean { return canEditTaskStatus(); }

  get isTaskP() { return this.selectedTask?.name === 'P'; }

  get isConfiguratoreEnabled() {
    // Integrazione col configuratore esterno spenta. Per le lead di tipo
    // 'configuratore' non c'era alcun permesso da controllare, quindi questo
    // e' l'unico punto che le ferma.
    if (!this.$store.getters.getIntegrations.mavigex.enabled) return false;
    if (!this.selectedLead) return false;
    if (this.networks.length === 0) return false;

    return (this.selectedLead.type === 'configuratore')
      || (this.selectedLead.type === 'standard'
      && this.hasPermission('leads_standard.task_preventivo_conf'));
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.resetDialog = true;
  }

  onComplete() {
    console.log(`${this.constructor.name}: onComplete()`); // eslint-disable-line no-console

    this.$emit('complete');
  }

  onSuperAction(action: string | null) {
    console.log(`${this.constructor.name}: onSuperAction() action = ${action}`); // eslint-disable-line no-console

    switch (action) {
      case 'force-complete':
        this.$emit('complete', true);
        break;

      default:
        this.$emit('super-action', action);
        break;
    }
  }

  onConfiguratore() {
    console.log(`${this.constructor.name}: onConfiguratore()`); // eslint-disable-line no-console

    this.$emit('configuratore');
  }

  onFollowUp() {
    console.log(`${this.constructor.name}: onFollowUp()`); // eslint-disable-line no-console

    this.$emit('followup');
  }

  onError() {
    console.log(`${this.constructor.name}: onError()`); // eslint-disable-line no-console

    this.$emit('error');
  }
}
